import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLoader } from './LoaderContext';
import './Carousel.css';

const SWIPE_THRESHOLD = 60;

function Carousel({ items }) {
  const [index, setIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const dragStartX = useRef(0);
  const { showLoader } = useLoader();

  const goTo = (i) => setIndex(Math.max(0, Math.min(items.length - 1, i)));

  const handlePointerDown = (e) => {
    if (e.target.closest('a, button')) return;
    dragStartX.current = e.clientX;
    setDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!dragging) return;
    setDragOffset(e.clientX - dragStartX.current);
  };

  const handleNavigate = (id) => {
    showLoader();
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 100)
  };

  const endDrag = () => {
    if (!dragging) return;
    if (dragOffset < -SWIPE_THRESHOLD) {
      goTo(index + 1);
    } else if (dragOffset > SWIPE_THRESHOLD) {
      goTo(index - 1);
    }
    setDragging(false);
    setDragOffset(0);
  };

  return (
    <div className="carousel">
      <button
        type="button"
        className="carousel-arrow carousel-arrow-left"
        onClick={() => goTo(index - 1)}
        disabled={index === 0}
        aria-label="Previous"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 24" class="carousel-arrow-svg-left">
          <path fill-rule="evenodd" d="M1.917 0L0 1.843 10.565 12 0 22.157 1.917 24 14.4 12z"></path>
        </svg>
      </button>

      <div
        className="carousel-viewport"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        <div
          className="carousel-track"
          style={{
            transform: `translateX(calc(${-index * 100}% + ${dragOffset}px))`,
            transition: dragging ? 'none' : 'transform 0.3s ease',
          }}
        >
          {items.map((item) => (
            <div className="carousel-card" key={item.id}>
              <img className="carousel-card-image" src={item.image} alt={item.alt} />
              <h3 className="carousel-card-header">{item.header}</h3>
              <p className="carousel-card-blurb">{item.blurb}</p>
              <Link className="carousel-card-button" to={item.buttonHref} onClick={() => handleNavigate(item.id)}>
                {item.buttonText}
              </Link>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        className="carousel-arrow carousel-arrow-right"
        onClick={() => goTo(index + 1)}
        disabled={index === items.length - 1}
        aria-label="Next"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 24" class="carousel-arrow-svg-right">
          <path fill-rule="evenodd" d="M1.917 0L0 1.843 10.565 12 0 22.157 1.917 24 14.4 12z"></path>
        </svg>
      </button>

      <div className="carousel-dots">
        {items.map((item, i) => (
          <button
            type="button"
            key={item.id}
            className={`carousel-dot${i === index ? ' active' : ''}`}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default Carousel;
